import { useState } from 'react';
import { MapPin, Loader2, CheckCircle2, XCircle, Truck, Banknote } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const PINCODE_KEY = 'last_pincode';

/**
 * Delivery serviceability for a pincode.
 *
 * Worth surfacing before checkout: without it a customer only discovers a
 * non-serviceable address after placing the order.
 */
export function PincodeCheck({ className = '' }) {
  const [pincode, setPincode] = useState(() => localStorage.getItem(PINCODE_KEY) || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const check = async (e) => {
    e?.preventDefault();
    const value = pincode.trim();

    if (!/^\d{6}$/.test(value)) {
      setResult(null);
      setError('Enter a valid 6-digit pincode');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${API}/shipping/serviceability?pincode=${value}`);
      if (!res.ok) throw new Error('Could not check this pincode right now');
      const data = await res.json();
      setResult(data);
      localStorage.setItem(PINCODE_KEY, value);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`bg-surface rounded-2xl p-4 ${className}`} data-testid="pincode-check">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-primary" />
        <p className="text-sm font-body font-semibold text-secondary">Check delivery</p>
      </div>

      <form onSubmit={check} className="flex gap-2">
        <Input
          value={pincode}
          onChange={(e) => {
            // Digits only — the API expects a bare 6-digit pincode.
            setPincode(e.target.value.replace(/\D/g, '').slice(0, 6));
            setResult(null);
            setError('');
          }}
          placeholder="Enter pincode"
          inputMode="numeric"
          className="rounded-xl bg-white h-10"
          data-testid="pincode-input"
        />
        <Button
          type="submit"
          variant="outline"
          disabled={loading || pincode.length !== 6}
          className="rounded-xl px-5 h-10 flex-shrink-0 bg-white"
          data-testid="pincode-check-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
        </Button>
      </form>

      {error && (
        <p className="text-xs font-body text-destructive mt-2.5">{error}</p>
      )}

      {result && (
        result.serviceable ? (
          <div className="mt-3 space-y-1.5">
            <p className="flex items-center gap-2 text-sm font-body font-medium text-success">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              Delivery available
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 pl-6">
              {result.eta_days != null && (
                <span className="flex items-center gap-1.5 text-xs font-body text-muted-foreground">
                  <Truck className="w-3.5 h-3.5" />
                  {result.eta_days === 0
                    ? 'Same day'
                    : `${result.eta_days} day${result.eta_days === 1 ? '' : 's'}`}
                </span>
              )}
              <span className="flex items-center gap-1.5 text-xs font-body text-muted-foreground">
                <Banknote className="w-3.5 h-3.5" />
                {result.cod_available ? 'Cash on delivery available' : 'Prepaid only'}
              </span>
            </div>
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm font-body font-medium text-destructive mt-3">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            We don't deliver to {pincode} yet
          </p>
        )
      )}
    </div>
  );
}
